import React, { useEffect, useState, useCallback } from "react";
import { CloseIcon } from "../Svgs";
import { Resizable } from "react-resizable";
import "./Console.scss";
import RawOutputList from "./RawOutputList";
import { base64ToHex } from "../../../../../../utils/commonFunctions";
import type { RawConsoleEntry } from "./types/consoleTypes";
import { OUTPUT_MODAL_HEIGHT_MIN, OUTPUT_MODAL_HEIGHT_MAX, OUTPUT_MODAL_WIDTH_MIN, OUTPUT_MODAL_WIDTH_MAX } from "./constants/consoleConstants";

interface IProps {
  loading: boolean;
  error: string | null;
  commandDetails: any | null;
  getCommandDetails: (commandId: string) => void;
  set_resizing: (resizing: boolean) => void;
}

/** Displays raw bytes table with resizable layout and hex output modal */
const RawOutputSection: React.FC<IProps> = ({ error, commandDetails, getCommandDetails, set_resizing }) => {

  const [outputModalSize, setOutputModalSize] = useState<{
    width: number;
    height: number;
    resizing: boolean | string;
  }>({
    width: 500,
    height: 1000,
    resizing: false,
  });

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [hexString, setHexString] = useState<string>("");
  const [isOutputModalVisible, setIsOutputModalVisible] = useState<boolean>(false);
  const [entries, setEntries] = useState<RawConsoleEntry[]>([]);

  // Reduced list height when output modal is visible (matches outputModalVisibleShortHeight CSS)
  const listDisplayHeight = isOutputModalVisible
    ? Math.max(150, Math.floor(window.innerHeight * 0.6325) - 280)
    : outputModalSize.height;

  // Converts the selected entry's base64 frame to hex for display
  const updateHexAndPayload = useCallback((selected_command: { CommandDetails?: { completeFrame?: string } }) => {
    const base64String = selected_command?.CommandDetails?.completeFrame ?? "";
    setHexString(base64ToHex(base64String));
    setIsOutputModalVisible(true);
  }, []);

  // Toggles row selection and populates or clears the hex output modal
  const show_selected_output_details = useCallback((selected_command: any) => {
    const already_selected = selectedRow?.CommandUid === selected_command?.CommandUid;
    setSelectedRow(already_selected ? null : selected_command);
    if (!already_selected) {
      updateHexAndPayload(selected_command);
    } else {
      setHexString("");
      setIsOutputModalVisible(false);
    }
  }, [selectedRow, updateHexAndPayload]);

  // Handles vertical resize of the output panel
  const onResizeHeight = useCallback((
    e: React.SyntheticEvent,
    { size }: { node: HTMLElement; size: { height: number }; handle: string }
  ) => {
    e.stopPropagation();
    if (size.height >= OUTPUT_MODAL_HEIGHT_MIN && size.height <= OUTPUT_MODAL_HEIGHT_MAX) {
      setOutputModalSize((prev) => ({ ...prev, height: size.height, resizing: "height" }));
    }
  }, []);


  // Resets resize state when drag ends
  const onResizeStop = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
    set_resizing(false);
    setOutputModalSize((prev) => ({ ...prev, resizing: false }));
  }, [set_resizing]);

  // Marks resize as active when drag starts
  const onResizeStart = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
    set_resizing(true);
  }, [set_resizing]);

  useEffect(() => {
    const clearFormFields = () => {
      setSelectedRow(null);
      setHexString("");
      setIsOutputModalVisible(false);
    };

    window.addEventListener("clearFormFields", clearFormFields);
    return () => {
      window.removeEventListener("clearFormFields", clearFormFields);
    };
  }, []);

  return (
    <div className={`right__panel__body__content__container ${isOutputModalVisible ? "outputModalVisibleShortHeight" : ""} right__panel__table__width modal__scrollbar__white__horizontal`}>
      <div className="content__container__table__header gap-3 justify-content-start">
        <div className="d-flex align-items-center justify-content-between" style={{ width: "160px" }}>
          <span>Received Time</span>
          {/* currently not having filters thing so commented this but in future when filter get updated then we will uncomment this */}
          {/* <button className="table__header__btn" disabled>
            <FilterSvg />
          </button> */}
        </div>
        <div className="d-flex align-items-center justify-content-between" style={{ width: "850px" }}>
          <span>Data</span>
        </div>
      </div>
      <Resizable
        className="position-relative"
        width={1200}
        height={listDisplayHeight}
        onResizeStop={onResizeStop}
        onResizeStart={onResizeStart}
        onResize={onResizeHeight}
        handle={(handleAxis, ref) => (
          <div ref={ref} className={`container__table__body__resize__handler ${outputModalSize.resizing === "height" ? "active" : ""} handle-${handleAxis}`} />
        )}
      >
        <div style={{ height: listDisplayHeight, overflowY: "hidden" }}>
          <div className="content__container__table__body">
            <RawOutputList
              height={listDisplayHeight}
              show_selected_output_details={show_selected_output_details}
              selectedRow={selectedRow}
              isOutputModalVisible={isOutputModalVisible}
              setEntries={setEntries}
              entries={entries}
            />
          </div>
        </div>
      </Resizable>
      {isOutputModalVisible && hexString && (
        <div className="output__modal">
          <div className="output__modal__header__console">
            <div className="output__modal__header__console__options__container">
              <button className="output__modal__header__console__option active">MESSAGES</button>
            </div>
            <div className="output__modal__header__console__actions_btns">
              {/* <button className="d-none" style={{ background: "transparent" }}>
                <Maximizeicon />
              </button> */}
              <button className="bg-transparent" onClick={() => setIsOutputModalVisible(false)}>
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className="output__modal__right__sectio w-100" style={{ overflowY: "auto", overflowX: "hidden" }}>
            <div
              onMouseDown={(e) => e.stopPropagation()}
              // @ts-expect-error onSelectStart is a non-standard but supported attribute
              onSelectStart={(e) => e.stopPropagation()}
            >
              {hexString || "Not available"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawOutputSection;
